import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { CurrencyProvider } from 'contexts/currencyContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { isLeFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../../contexts/ItineraryContext'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import FlightLegRow, { LegSeparator } from './ShortlistedFlightLegRow'
import { isBookedFlightOption, parseSearchParams, resolveShortlistedFlightPriceAndCurrency } from './flightShortlistUtils'
import useFlightShortlistExpiry from './useFlightShortlistExpiry'
import {
  formatOccupantsLabel,
  getSavedFlightLegDate,
  type LegData,
} from './shortlistedFlightLegData'

const DAY_MONTH_YEAR_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}

const CardContainer = styled(Sheet)<{ $isExpired: boolean }>`
  opacity: ${props => props.$isExpired ? 0.7 : 1};
`

interface Props {
  /** Every leg of one multi-city itinerary, in leg order. */
  legs: Array<TransportOption>
  isBooking: boolean
  isSearching: boolean
  getRefreshedData: (option: TransportOption) => RefreshedFlight | undefined
  onBookNow: (legs: Array<TransportOption>) => void
  onDelete: (legs: Array<TransportOption>) => void
  onRefresh: () => void
}

/**
 * A multi-city itinerary persists one trip item per leg, so the shortlist holds
 * several options that belong to a single booking. This renders them as the one
 * card the user thinks of them as: every leg, one combined price, one set of
 * actions.
 */
function ShortlistedMultiCityFlightCard({
  legs,
  isBooking,
  isSearching,
  getRefreshedData,
  onBookNow,
  onDelete,
  onRefresh,
}: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const deleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const firstLeg = legs[0]
  const firstLegData = isLeFlightSavedItemData(firstLeg?.savedItemData) ? firstLeg.savedItemData : undefined
  const searchParams = useMemo(
    () => firstLegData ? parseSearchParams(firstLegData.searchParams) : undefined,
    [firstLegData],
  )

  // The group can only be booked while every leg is still live, so the soonest
  // leg expiry governs the whole card.
  const earliestExpiry = useMemo(() => {
    const expiries = legs
      .map(leg => {
        const refreshed = getRefreshedData(leg)
        const data = isLeFlightSavedItemData(leg.savedItemData) ? leg.savedItemData : undefined
        return refreshed?.newExpiry ?? data?.searchExpiry
      })
      .filter((expiry): expiry is number => expiry != null)
    return expiries.length ? Math.min(...expiries) : undefined
  }, [getRefreshedData, legs])
  const { isExpired: timerExpired } = useFlightShortlistExpiry(earliestExpiry)
  const isSoldOut = legs.some(leg => getRefreshedData(leg)?.isSoldOut)
  const isExpired = timerExpired || isSoldOut
  const isBooked = legs.some(isBookedFlightOption)

  const legRows = useMemo<Array<{ key: number; leg: LegData }>>(
    () => legs.flatMap((option) => {
      const data = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
      if (!data) return []

      return [{
        key: option.itemId,
        leg: {
          airline: data.airline,
          flightNumber: data.flightNumber,
          departureAirport: data.departureAirport,
          arrivalAirport: data.arrivalAirport,
          displayDate: getSavedFlightLegDate(data, false),
          departureTime: data.departureTime,
          arrivalTime: data.arrivalTime,
          duration: data.duration,
          layovers: data.layovers,
          carrierLogo: data.carrierLogo,
        },
      }]
    }),
    [legs],
  )

  // All-or-nothing, matching how the cart totals a multi-city group: a total
  // that silently omits a leg reads as a complete price but isn't one.
  const { totalPrice, totalRegularPrice, isLuxPlusFare, priceCurrency } = useMemo(() => {
    let total = 0
    let regularTotal = 0
    let currency: string | undefined
    let allLuxPlus = true

    for (const option of legs) {
      const data = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
      const price = resolveShortlistedFlightPriceAndCurrency({
        isReturn: false,
        savedItemData: data,
        refreshedData: getRefreshedData(option),
      })

      if (price.displayPrice == null) return {}
      total += price.displayPrice
      regularTotal += price.regularPrice ?? price.displayPrice
      allLuxPlus = allLuxPlus && price.isLuxPlusFare === true
      currency = currency ?? price.priceCurrency
    }

    return {
      totalPrice: total,
      totalRegularPrice: regularTotal,
      isLuxPlusFare: allLuxPlus,
      priceCurrency: currency,
    }
  }, [getRefreshedData, legs])

  const subtitle = useMemo(() => {
    const parts: Array<string> = []
    const itinerary = searchParams?.itinerary

    if (itinerary?.length) {
      const first = itinerary[0]
      const last = itinerary[itinerary.length - 1]
      const startDate = dateFormatter(first.departureDate, DAY_MONTH_YEAR_FORMAT)
      const endDate = dateFormatter(last.departureDate, DAY_MONTH_YEAR_FORMAT)
      parts.push(startDate === endDate ? startDate : `${startDate} - ${endDate}`)
      parts.push(intl.formatMessage(
        {
          id: 'pmeuxOyYKH5ZCNDyJkFw',
          defaultMessage: '{origin} to {destination}',
          description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
        },
        { origin: first.departureAirport, destination: last.arrivalAirport },
      ))
    }

    const occupantsLabel = formatOccupantsLabel(searchParams?.occupancies, intl)
    if (occupantsLabel) parts.push(occupantsLabel)

    return parts.join(' · ')
  }, [dateFormatter, intl, searchParams])

  const toggleDropdown = useCallback(() => setIsDropdownOpen(prev => !prev), [])
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), [])

  const handleDelete = useCallback(() => {
    closeDropdown()
    onDelete(legs)
  }, [closeDropdown, legs, onDelete])

  const handleBookNow = useCallback(() => onBookNow(legs), [legs, onBookNow])

  if (!firstLegData || legRows.length === 0) return null

  const headerTitle = intl.formatMessage({
    id: 'dz09_xQn8lod-1xXWD9Q',
    defaultMessage: 'Multi-city flights',
    description: 'Header title for a multi-city flight card in the shortlisted flights section',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'f927PFvDhEB4UNtbgo10',
    defaultMessage: 'Book now',
    description: 'Button to book a shortlisted multi-city flight in trip planner',
  })

  const checkingAvailabilityLabel = intl.formatMessage({
    id: 'CnAVHzAzv6cickngcpgZ',
    defaultMessage: 'Checking availability',
    description: 'Status shown while re-checking availability of a shortlisted multi-city flight',
  })

  const deleteLabel = intl.formatMessage({
    id: 'WDP4XvTE5X4MdRvIpyBH',
    defaultMessage: 'Delete',
    description: 'Dropdown menu item to delete a shortlisted multi-city flight from the trip planner',
  })

  const expiredMessage = intl.formatMessage({
    id: 'h_IySEOmLkSodOQpQyf_',
    defaultMessage: 'This flight price has expired',
    description: 'Banner shown when a shortlisted multi-city flight price has expired',
  })

  const refreshLabel = intl.formatMessage({
    id: 'SSM-PJcRj2fgtyuDVNgs',
    defaultMessage: 'Refresh',
    description: 'Button to refresh an expired shortlisted multi-city flight price in trip planner',
  })

  const canRenderPrice = totalPrice != null && !!priceCurrency
  const showLuxPlusPrice = isLuxPlusFare === true && Number.isFinite(totalRegularPrice)
  const showBookNow = !isExpired && !isBooked

  return (
    <CardContainer border="light-foreground" borderRadius="L" padding={16} $isExpired={isExpired}>
      <Group direction="vertical" gap={12} fullWidth>
        <Group direction="vertical" gap={16} fullWidth>
          <Group direction="horizontal" gap={8} verticalAlign="start" horizontalAlign="space-between" fullWidth>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6">{headerTitle}</Heading>
              <Caption variant="medium" colour="light-foreground-alt">{subtitle}</Caption>
            </Group>
            <IconButton
              size="small"
              kind="tertiary"
              onClick={toggleDropdown}
              ref={dropdownTriggerRef}
              disabled={deleteActionsDisabled}
            >
              <LineEllipsisVIcon />
            </IconButton>
            <ResponsiveDropdownList
              size="S"
              open={isDropdownOpen}
              onClose={closeDropdown}
              triggerRef={dropdownTriggerRef}
              anchorRef={dropdownTriggerRef}
              placement="bottom-end"
              portaled
            >
              <ClickableListItem
                title={deleteLabel}
                size="medium"
                startIcon={<LineTrashAltIcon size="S" />}
                onClick={handleDelete}
              />
            </ResponsiveDropdownList>
          </Group>

          <LegSeparator />

          {legRows.map(({ key, leg }) => <FlightLegRow key={key} leg={leg} />)}

          <LegSeparator />

          <Group
            direction="horizontal"
            gap={8}
            verticalAlign="center"
            horizontalAlign={canRenderPrice ? 'space-between' : 'end'}
            fullWidth
          >
            {canRenderPrice && (
              <CurrencyProvider currency={priceCurrency}>
                {showLuxPlusPrice ?
                    <PriceRowLuxPlusPrice size="M" memberPrice={totalPrice} nonMemberPrice={totalRegularPrice} saleUnit="adult" /> :
                    <PriceRowPrice size="M" price={totalPrice} saleUnit="adult" />}
              </CurrencyProvider>
            )}

            {showBookNow && (isSearching ? (
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LoadingSpinner size={16} inline />
                <Caption variant="medium" colour="light-foreground-alt">{checkingAvailabilityLabel}</Caption>
              </Group>
            ) : (
              <TextButton kind="primary" size="small" onClick={handleBookNow} disabled={isBooking}>
                {bookNowLabel}
              </TextButton>
            ))}
          </Group>
        </Group>

        {isExpired && !isBooked && (
          <MessageBanner
            kind="warning"
            density="compact"
            icon={<LineInfoCircleIcon size="S" />}
            primaryActionButtonProps={{
              children: refreshLabel,
              onClick: onRefresh,
              size: 'small',
            }}
          >
            {expiredMessage}
          </MessageBanner>
        )}
      </Group>
    </CardContainer>
  )
}

export default memo(ShortlistedMultiCityFlightCard)
